import React, { useEffect, useState } from 'react';
import { ChevronDown, ChevronRight, AlertTriangle, Download, ArrowUp, ArrowDown } from 'lucide-react';
import { StatTile, MiniBarChart } from '../../components/charts/MiniCharts';
import DataGrid, { type GridColumn } from '../../components/DataGrid';
import { cloudUsage, type CloudUsageReport, type CloudRunServiceInfo, type ApiError } from '../../services/api';
import { downloadBlob } from '../../utils/downloadBlob';

const fmtNumber = (n: number) => n.toLocaleString('en-IN', { maximumFractionDigits: 0 });

const fmtBytes = (n: number) => {
  const gib = n / 1_073_741_824;
  return `${gib.toFixed(2)} GiB`;
};

const pct = (used: number, cap: number) => (cap === 0 ? 0 : Math.round((used / cap) * 100));

const panelDesc = {
  fontSize: 11.5,
  color: 'var(--nx-text-muted)',
  marginBottom: 14,
  lineHeight: 1.4,
} as const;

const MonthDelta: React.FC<{ current: number; previous: number }> = ({ current, previous }) => {
  if (previous === 0) return <span style={{ fontSize: 11, color: 'var(--nx-text-muted)' }}>no prior-month data</span>;
  const change = Math.round(((current - previous) * 100) / previous);
  const up = change > 0;
  const flat = change === 0;
  const color = flat ? 'var(--nx-text-muted)' : up ? '#DC2626' : '#059669';
  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 2, fontSize: 11, color, fontWeight: 600 }}>
      {!flat && (up ? <ArrowUp size={11} /> : <ArrowDown size={11} />)}
      {flat ? '0%' : `${Math.abs(change)}%`} vs last month
    </span>
  );
};

const serviceColumns: GridColumn<CloudRunServiceInfo>[] = [
  { key: 'name', header: 'Service', render: s => s.name },
  { key: 'region', header: 'Region', render: s => s.region },
  {
    key: 'scaling',
    header: 'Min / Max Instances',
    render: s => (
      <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
        {s.minInstances}&nbsp;/&nbsp;{s.maxInstances}
        {s.minInstances > 0 && (
          <span title="min-instances > 0 bills continuously, even with zero traffic">
            <AlertTriangle size={13} color="#DC2626" />
          </span>
        )}
      </span>
    ),
  },
  { key: 'cpu', header: 'CPU', render: s => s.cpu || '—' },
  { key: 'memory', header: 'Memory', render: s => s.memory || '—' },
  {
    key: 'currentInstanceCount',
    header: 'Running Now',
    render: s => (
      <span style={{ fontWeight: 600, color: s.currentInstanceCount > 0 ? 'var(--nx-text-primary)' : 'var(--nx-text-muted)' }}>
        {s.currentInstanceCount}
      </span>
    ),
  },
  { key: 'deploymentCount', header: 'Deploys (mo.)', render: s => s.deploymentCount },
];

const CloudUsagePage: React.FC = () => {
  const [report, setReport] = useState<CloudUsageReport | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState<string | null>(null);
  const [exporting, setExporting] = useState(false);

  useEffect(() => {
    cloudUsage
      .get()
      .then(setReport)
      .catch((e: ApiError) => setError(e.message || 'Failed to load cloud usage report'))
      .finally(() => setLoading(false));
  }, []);

  const handleExport = async () => {
    setExporting(true);
    try {
      const blob = await cloudUsage.exportExcel();
      downloadBlob(blob, 'cloud-usage-report.xlsx');
    } catch {
      setError('Failed to export report');
    } finally {
      setExporting(false);
    }
  };

  return (
    <div style={{ background: '#f8fafc', minHeight: '100%', padding: 32, fontFamily: 'var(--font-body)' }}>
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 16 }}>
        <div>
          <h1 style={{ fontFamily: 'Syne, sans-serif', fontSize: 22, fontWeight: 800, color: 'var(--nx-text-primary)', letterSpacing: '-0.03em', marginBottom: 8 }}>
            Cloud Usage
          </h1>
          <p style={{ fontSize: 13, color: 'var(--nx-text-muted)', marginBottom: 24, maxWidth: 640 }}>
            Cloud Run usage for the current calendar month against GCP's Always Free tier. These caps are
            pooled across the whole billing account, not per project — the per-project bars below are for
            visibility only, not individual quotas.
          </p>
        </div>
        {report && (
          <button
            onClick={handleExport}
            disabled={exporting}
            style={{
              display: 'flex', alignItems: 'center', gap: 8, padding: '9px 16px',
              background: '#1A6CF6', color: '#fff', border: 'none', borderRadius: 8,
              fontSize: 13, fontWeight: 600, cursor: exporting ? 'default' : 'pointer',
              opacity: exporting ? 0.6 : 1, fontFamily: 'var(--font-body)', flexShrink: 0,
            }}
          >
            <Download size={15} />
            {exporting ? 'Exporting…' : 'Export to Excel'}
          </button>
        )}
      </div>

      {loading && <p style={{ fontSize: 13, color: 'var(--nx-text-muted)' }}>Loading…</p>}
      {error && <p style={{ fontSize: 13, color: '#DC2626' }}>{error}</p>}

      {report && (
        <>
          <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap', marginBottom: 12 }}>
            <StatTile
              label="Requests"
              value={fmtNumber(report.requests.used)}
              sub={`${pct(report.requests.used, report.requests.cap)}% of ${fmtNumber(report.requests.cap)} free`}
              accent={pct(report.requests.used, report.requests.cap) >= 100 ? '#DC2626' : '#1A6CF6'}
            />
            <StatTile
              label="vCPU-seconds"
              value={fmtNumber(report.vCpuSeconds.used)}
              sub={`${pct(report.vCpuSeconds.used, report.vCpuSeconds.cap)}% of ${fmtNumber(report.vCpuSeconds.cap)} free`}
              accent={pct(report.vCpuSeconds.used, report.vCpuSeconds.cap) >= 100 ? '#DC2626' : '#1A6CF6'}
            />
            <StatTile
              label="Memory GiB-seconds"
              value={fmtNumber(report.memoryGibSeconds.used)}
              sub={`${pct(report.memoryGibSeconds.used, report.memoryGibSeconds.cap)}% of ${fmtNumber(report.memoryGibSeconds.cap)} free`}
              accent={pct(report.memoryGibSeconds.used, report.memoryGibSeconds.cap) >= 100 ? '#DC2626' : '#1A6CF6'}
            />
            <StatTile
              label="Network Egress"
              value={fmtBytes(report.networkEgressBytes.used)}
              sub={`${pct(report.networkEgressBytes.used, report.networkEgressBytes.cap)}% of ${fmtBytes(report.networkEgressBytes.cap)} free`}
              accent={pct(report.networkEgressBytes.used, report.networkEgressBytes.cap) >= 100 ? '#DC2626' : '#1A6CF6'}
            />
          </div>
          <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap', marginBottom: 12 }}>
            <div style={{ flex: 1, minWidth: 160 }}><MonthDelta current={report.requests.used} previous={report.previousMonth.requests} /></div>
            <div style={{ flex: 1, minWidth: 160 }}><MonthDelta current={report.vCpuSeconds.used} previous={report.previousMonth.vCpuSeconds} /></div>
            <div style={{ flex: 1, minWidth: 160 }}><MonthDelta current={report.memoryGibSeconds.used} previous={report.previousMonth.memoryGibSeconds} /></div>
            <div style={{ flex: 1, minWidth: 160 }}><MonthDelta current={report.networkEgressBytes.used} previous={report.previousMonth.networkEgressBytes} /></div>
          </div>

          <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap', marginBottom: 8 }}>
            <StatTile label="Cloud Build Runs (this month)" value={fmtNumber(report.totalBuildCount)} accent="#6D28D9" />
            <StatTile label="Cloud Build Minutes (this month)" value={fmtNumber(report.totalBuildMinutes)} accent="#6D28D9" />
            <StatTile label="Deployments (this month)" value={fmtNumber(report.totalDeploymentCount)} accent="#059669" />
          </div>
          <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap', marginBottom: 32 }}>
            <div style={{ flex: 1, minWidth: 160 }}><MonthDelta current={report.totalBuildCount} previous={report.previousMonth.buildCount} /></div>
            <div style={{ flex: 1, minWidth: 160 }}><MonthDelta current={report.totalBuildMinutes} previous={report.previousMonth.buildMinutes} /></div>
            <div style={{ flex: 1, minWidth: 160 }} />
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 24, marginBottom: 32 }}>
            <div style={{ background: 'var(--nx-surface-1)', border: '1px solid var(--nx-border)', borderRadius: 12, padding: 18 }}>
              <p style={{ fontSize: 12, fontWeight: 700, color: 'var(--nx-text-secondary)', marginBottom: 6 }}>Requests by project</p>
              <p style={panelDesc}>Every HTTP call handled by a Cloud Run service in that project this month — one page load can fire several.</p>
              <MiniBarChart
                data={report.projects.map(p => ({ label: p.projectId, value: p.requests }))}
                formatValue={fmtNumber}
              />
            </div>
            <div style={{ background: 'var(--nx-surface-1)', border: '1px solid var(--nx-border)', borderRadius: 12, padding: 18 }}>
              <p style={{ fontSize: 12, fontWeight: 700, color: 'var(--nx-text-secondary)', marginBottom: 6 }}>vCPU-seconds by project</p>
              <p style={panelDesc}>CPU time actually billed while requests were processing (vCPUs allocated × seconds active) — idle time isn't counted unless min-instances keeps a container warm.</p>
              <MiniBarChart
                data={report.projects.map(p => ({ label: p.projectId, value: p.vCpuSeconds }))}
                formatValue={fmtNumber}
                color="#6D28D9"
              />
            </div>
            <div style={{ background: 'var(--nx-surface-1)', border: '1px solid var(--nx-border)', borderRadius: 12, padding: 18 }}>
              <p style={{ fontSize: 12, fontWeight: 700, color: 'var(--nx-text-secondary)', marginBottom: 6 }}>Memory GiB-seconds by project</p>
              <p style={panelDesc}>Memory reserved for a container × seconds it was active. A project with a service kept always-on (min-instances &gt; 0) shows up disproportionately high here.</p>
              <MiniBarChart
                data={report.projects.map(p => ({ label: p.projectId, value: p.memoryGibSeconds }))}
                formatValue={fmtNumber}
                color="#059669"
              />
            </div>
            <div style={{ background: 'var(--nx-surface-1)', border: '1px solid var(--nx-border)', borderRadius: 12, padding: 18 }}>
              <p style={{ fontSize: 12, fontWeight: 700, color: 'var(--nx-text-secondary)', marginBottom: 6 }}>Egress by project</p>
              <p style={panelDesc}>Data sent back to users/clients over the internet from that project's services — response bodies, downloads, images, etc.</p>
              <MiniBarChart
                data={report.projects.map(p => ({ label: p.projectId, value: p.networkEgressBytes }))}
                formatValue={fmtBytes}
                color="#D97706"
              />
            </div>
            <div style={{ background: 'var(--nx-surface-1)', border: '1px solid var(--nx-border)', borderRadius: 12, padding: 18 }}>
              <p style={{ fontSize: 12, fontWeight: 700, color: 'var(--nx-text-secondary)', marginBottom: 6 }}>Cloud Build runs by project</p>
              <p style={panelDesc}>Number of builds triggered this month — each push to develop/stage/main runs one. Cloud Build's free tier (120 min/day) is also pooled across the billing account.</p>
              <MiniBarChart
                data={report.projects.map(p => ({ label: p.projectId, value: p.buildCount }))}
                formatValue={fmtNumber}
                color="#6D28D9"
              />
            </div>
            <div style={{ background: 'var(--nx-surface-1)', border: '1px solid var(--nx-border)', borderRadius: 12, padding: 18 }}>
              <p style={{ fontSize: 12, fontWeight: 700, color: 'var(--nx-text-secondary)', marginBottom: 6 }}>Deployments by project</p>
              <p style={panelDesc}>New Cloud Run revisions created this month across that project's services — how often it's actually being deployed to.</p>
              <MiniBarChart
                data={report.projects.map(p => ({ label: p.projectId, value: p.deploymentCount }))}
                formatValue={fmtNumber}
                color="#059669"
              />
            </div>
          </div>

          <div>
            <p style={{ fontSize: 12, fontWeight: 700, color: 'var(--nx-text-secondary)', marginBottom: 6 }}>Cloud Run services by project</p>
            <p style={panelDesc}>Live scaling config and current instance count per project — click a project to expand. A flagged service (min-instances &gt; 0) keeps at least one container running 24/7, billing continuously regardless of traffic.</p>

            <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
              {report.projects.map(p => {
                const isOpen = expanded === p.projectId;
                const flaggedCount = p.services.filter(s => s.minInstances > 0).length;
                return (
                  <div key={p.projectId} style={{ background: 'var(--nx-surface-1)', border: '1px solid var(--nx-border)', borderRadius: 12, overflow: 'hidden' }}>
                    <button
                      onClick={() => setExpanded(isOpen ? null : p.projectId)}
                      style={{
                        width: '100%', display: 'flex', alignItems: 'center', gap: 10,
                        padding: '14px 18px', background: 'none', border: 'none', cursor: 'pointer',
                        textAlign: 'left', fontFamily: 'var(--font-body)',
                      }}
                    >
                      {isOpen ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
                      <span style={{ fontSize: 13, fontWeight: 600, color: 'var(--nx-text-primary)' }}>{p.projectId}</span>
                      <span style={{ fontSize: 12, color: 'var(--nx-text-muted)' }}>
                        {p.services.length} service{p.services.length === 1 ? '' : 's'}
                      </span>
                      <span style={{ fontSize: 12, color: 'var(--nx-text-muted)' }}>
                        {p.buildCount} builds · {p.deploymentCount} deploys this month
                      </span>
                      {flaggedCount > 0 && (
                        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, fontSize: 12, color: '#DC2626', marginLeft: 'auto' }}>
                          <AlertTriangle size={13} />
                          {flaggedCount} always-on
                        </span>
                      )}
                    </button>
                    {isOpen && (
                      <div style={{ borderTop: '1px solid var(--nx-border)' }}>
                        <DataGrid
                          columns={serviceColumns}
                          rows={p.services}
                          rowKey={s => `${p.projectId}-${s.region}-${s.name}`}
                          emptyText="No Cloud Run services in this project."
                        />
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default CloudUsagePage;
